'use client'

import { useEffect, useMemo, useState } from 'react'
import { useFunnelConfig, ConfigProduct } from '@/lib/hooks/useFunnelConfig'

export interface BundleCartPayload {
  bundleSlug: string
  bundleName: string
  baseWcIds: number[]
  // Base layers the customer swapped (e.g. the bag's own flap). The connector
  // only accepts these for categories that are also a slot source.
  baseOverrides: Array<{
    categoryId: string
    productWcId: number
  }>
  slotPicks: Array<{
    slotId: string
    slotName: string
    productWcId: number
  }>
  extras: Array<{
    categoryId: string
    categoryName: string
    productWcId: number
  }>
}

interface ExtraItem {
  id: string         // local instance id
  categoryId: string
  productId: string  // selected product
}

const generateExtraId = () => `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

interface BundleProps {
  slug: string
  onAddToCart?: (payload: BundleCartPayload) => void
  cartLoading?: boolean
  cartAdded?: boolean
}

export function Bundle({ slug, onAddToCart, cartLoading, cartAdded }: BundleProps) {
  const { config, isLoading, isError } = useFunnelConfig(slug)
  const [slotSelections, setSlotSelections] = useState<Record<string, string>>({})
  const [baseOverrides, setBaseOverrides] = useState<Record<string, string>>({})
  const [extrasOpen, setExtrasOpen] = useState(false)
  const [extras, setExtras] = useState<ExtraItem[]>([])

  // Initialize slot selections from defaults when config loads
  useEffect(() => {
    if (!config?.bundleConfig) return
    const initial: Record<string, string> = {}
    for (const slot of config.bundleConfig.slots) {
      initial[slot.id] = slot.defaultProductId
    }
    setSlotSelections(initial)
  }, [config?.bundleConfig])

  // Base bag categories the customer may choose (those also offered as a
  // slot, e.g. the flap) — the rest of the bag stays imposed.
  const selectableBaseCategoryIds = useMemo(() => {
    const ids = new Set<string>()
    if (!config?.bundleConfig) return ids
    for (const slot of config.bundleConfig.slots) {
      if (config.bundleConfig.baseLayerPicks[slot.sourceCategoryId]) ids.add(slot.sourceCategoryId)
    }
    return ids
  }, [config?.bundleConfig])

  // Admin base picks with the customer's choices applied on top
  const basePicks = useMemo<Record<string, string>>(() => {
    if (!config?.bundleConfig) return {}
    return { ...config.bundleConfig.baseLayerPicks, ...baseOverrides }
  }, [config?.bundleConfig, baseOverrides])

  // Resolve product map from categories
  const productMap = useMemo(() => {
    const m = new Map<string, ConfigProduct>()
    if (!config) return m
    for (const cat of config.categories) {
      for (const p of cat.products) m.set(p.id, p)
    }
    return m
  }, [config])

  // Base bag layers (sorted by z-index)
  const baseBagLayers = useMemo(() => {
    if (!config) return []
    const layers: Array<{ image: string; layer: number; key: string; opacity?: number }> = []
    if (config.baseImage) layers.push({ image: config.baseImage, layer: 0, key: 'base' })
    for (const [catId, productId] of Object.entries(basePicks)) {
      const p = productMap.get(productId)
      if (p?.image) layers.push({ image: p.image, layer: p.layer, key: catId })
    }
    return layers.sort((a, b) => a.layer - b.layer)
  }, [config, productMap, basePicks])

  // Slot products cache (preload candidates)
  const slotProductsCache = useMemo(() => {
    const cache = new Map<string, ConfigProduct[]>()
    if (!config?.bundleConfig) return cache
    for (const slot of config.bundleConfig.slots) {
      const cat = config.categories.find(c => c.id === slot.sourceCategoryId)
      cache.set(slot.id, cat?.products || [])
    }
    return cache
  }, [config])

  // Preload all slot product images on mount (so dropdown switching is instant)
  useEffect(() => {
    if (typeof window === 'undefined') return
    for (const products of slotProductsCache.values()) {
      for (const p of products) {
        if (p.image) {
          const img = new Image()
          img.src = p.image
        }
      }
    }
  }, [slotProductsCache])

  if (isLoading) {
    return <div className="p-12 text-center text-gray-500">Chargement…</div>
  }
  if (isError || !config) {
    return <div className="p-12 text-center text-red-600">Erreur de chargement du lot.</div>
  }
  if (config.type !== 'bundle' || !config.bundleConfig) {
    return <div className="p-12 text-center text-gray-600">Ce funnel n&apos;est pas configuré comme lot.</div>
  }

  const { bundleConfig } = config
  const fmt = (cents: number) => `${(cents / 100).toFixed(2)} €`

  // The L (Grand) bag is wider than its source canvas allows; the default
  // 1.2x desktop zoom crops the sides. Use scale-100 for L only.
  const imgScaleClass = config.size === 'l' ? 'md:scale-100' : 'md:scale-[1.2]'

  // Selected product per slot
  const slotSelectedProducts = bundleConfig.slots.map(slot => ({
    slot,
    product: productMap.get(slotSelections[slot.id] || slot.defaultProductId),
  }))

  // Pricing
  const baseLayerSum = Object.values(basePicks)
    .map(pid => productMap.get(pid))
    .filter((p): p is ConfigProduct => !!p)
    .reduce((sum, p) => sum + (p.price || 0), 0)
  const slotsSum = slotSelectedProducts
    .map(({ product }) => product?.price || 0)
    .reduce((a, b) => a + b, 0)
  const extrasSum = extras
    .map(e => productMap.get(e.productId))
    .filter((p): p is ConfigProduct => !!p)
    .reduce((sum, p) => sum + (p.price || 0), 0)
  const subtotal = (config.basePrice || 0) + baseLayerSum + slotsSum + extrasSum
  const discounted = subtotal * (1 - bundleConfig.discountPercent / 100)
  const savings = subtotal - discounted

  // Compute slot preview layers: the slot's selected product replaces the
  // base bag layer that came from the SAME source category.
  const slotPreviewLayers = (slotIdx: number) => {
    const { slot, product } = slotSelectedProducts[slotIdx]
    const layers = baseBagLayers.slice()
    if (!product?.image) return layers

    const basePickId = basePicks[slot.sourceCategoryId]
    const basePickProduct = basePickId ? productMap.get(basePickId) : undefined
    const swapZ = basePickProduct?.layer

    if (swapZ !== undefined) {
      const filtered = layers.filter(l => l.layer !== swapZ)
      filtered.push({ image: product.image, layer: swapZ, key: `slot-${slot.id}` })
      return filtered.sort((a, b) => a.layer - b.layer)
    }
    return [...layers, { image: product.image, layer: 9999, key: `slot-${slot.id}` }]
  }

  const handleAddToCart = () => {
    if (!onAddToCart) return
    const baseWcIds: number[] = []
    if (config.baseWcId) baseWcIds.push(config.baseWcId)
    for (const productId of Object.values(basePicks)) {
      const p = productMap.get(productId)
      if (p?.wcId) baseWcIds.push(p.wcId)
    }
    const baseOverridePayload = Object.entries(baseOverrides)
      .filter(([catId]) => selectableBaseCategoryIds.has(catId))
      .map(([categoryId, productId]) => ({
        categoryId,
        productWcId: productMap.get(productId)?.wcId || 0,
      }))
      .filter(o => o.productWcId > 0)
    const slotPicks = slotSelectedProducts
      .map(({ slot, product }) => ({
        slotId: slot.id,
        slotName: slot.name,
        productWcId: product?.wcId || 0,
      }))
      .filter(s => s.productWcId > 0)
    const extraPayload = extras
      .map(e => {
        const cat = config.categories.find(c => c.id === e.categoryId)
        const product = productMap.get(e.productId)
        return {
          categoryId: e.categoryId,
          categoryName: cat?.name || '',
          productWcId: product?.wcId || 0,
        }
      })
      .filter(e => e.productWcId > 0)
    onAddToCart({
      bundleSlug: config.slug,
      bundleName: config.name,
      baseWcIds,
      baseOverrides: baseOverridePayload,
      slotPicks,
      extras: extraPayload,
    })
  }

  // Compute productIds already used (base picks, slots, extras), by category
  const usedProductIdsByCategory = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const [catId, productId] of Object.entries(basePicks)) {
      if (!map.has(catId)) map.set(catId, new Set())
      map.get(catId)!.add(productId)
    }
    for (const { slot, product } of slotSelectedProducts) {
      if (!product) continue
      if (!map.has(slot.sourceCategoryId)) map.set(slot.sourceCategoryId, new Set())
      map.get(slot.sourceCategoryId)!.add(product.id)
    }
    for (const e of extras) {
      if (!map.has(e.categoryId)) map.set(e.categoryId, new Set())
      map.get(e.categoryId)!.add(e.productId)
    }
    return map
  }, [basePicks, slotSelectedProducts, extras])

  // Extras handlers
  const addExtra = (categoryId: string) => {
    const cat = config.categories.find(c => c.id === categoryId)
    if (!cat) return
    const usedIds = usedProductIdsByCategory.get(categoryId) || new Set()
    const defaultProduct = cat.products.find(p => !usedIds.has(p.id))
    if (!defaultProduct) return // category exhausted, no add
    setExtras(prev => [
      ...prev,
      {
        id: generateExtraId(),
        categoryId,
        productId: defaultProduct.id,
      },
    ])
  }
  const updateExtra = (extraId: string, productId: string) => {
    setExtras(prev => prev.map(e => (e.id === extraId ? { ...e, productId } : e)))
  }
  const removeExtra = (extraId: string) => {
    setExtras(prev => prev.filter(e => e.id !== extraId))
  }

  return (
    <div className="bundle-root font-[family-name:var(--font-poppins)] w-full max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-12 text-gray-900 pb-28 md:pb-12">
      {/* DOM order is column-by-column (mobile-natural). Explicit row/col
          placement on md+ rebuilds the desktop grid layout. */}
      <div className="grid grid-cols-1 gap-y-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-x-3 lg:md:gap-x-4 md:gap-y-3 md:items-start">
        {/* ───── Col 1: Sac inclus ───── */}
        <div className="md:col-start-1 md:row-start-1 text-center md:min-h-[4em] md:flex md:flex-col md:items-center md:justify-end">
          <h3 className="uppercase text-xl md:text-[10px] tracking-[0.2em] font-semibold text-gray-700">
            Sac inclus
          </h3>
          <PriceLine
            original={(config.basePrice || 0) + baseLayerSum}
            discountPercent={bundleConfig.discountPercent}
            fmt={fmt}
            className="mt-1"
          />
        </div>
        <div className="md:col-start-1 md:row-start-2 aspect-square overflow-hidden">
          <LayeredImage layers={baseBagLayers} alt={config.name} scaleClass={imgScaleClass} />
        </div>
        <div className="md:col-start-1 md:row-start-3 md:mb-0">
          {config.categories
            .filter(cat => selectableBaseCategoryIds.has(cat.id))
            .map(cat => (
              <div key={cat.id} className="mb-2">
                <SlotDropdown
                  slot={{ id: `base-${cat.id}`, name: cat.name }}
                  selectedProductId={basePicks[cat.id]}
                  products={cat.products}
                  onChange={(productId) => setBaseOverrides(o => ({ ...o, [cat.id]: productId }))}
                />
              </div>
            ))}
          <ul className="py-2 text-center text-sm font-medium space-y-1 list-none px-2 m-0">
            {config.baseName && <li>{config.baseName}</li>}
            {config.categories.map(cat => {
              if (selectableBaseCategoryIds.has(cat.id)) return null
              const productId = basePicks[cat.id]
              if (!productId) return null
              const p = productMap.get(productId)
              return p ? <li key={cat.id}>{p.name}</li> : null
            })}
          </ul>
        </div>

        {/* + separator (desktop only) */}
        <div className="flex items-center justify-center md:col-start-2 md:row-start-2 md:w-6 lg:w-8 md:self-center my-5 md:my-0">
          <span className="text-4xl md:text-4xl lg:text-5xl text-gray-300 font-light leading-none">+</span>
        </div>

        {/* ───── Col 2: Choix 2ème rabat ───── */}
        <div className="md:col-start-3 md:row-start-1 text-center md:min-h-[4em] md:flex md:flex-col md:items-center md:justify-end">
          <h3 className="uppercase text-xl md:text-[10px] tracking-[0.2em] font-semibold text-gray-700">
            Choix 2ème rabat
          </h3>
          <PriceLine
            original={slotSelectedProducts[0]?.product?.price || 0}
            discountPercent={bundleConfig.discountPercent}
            fmt={fmt}
            className="mt-1"
          />
        </div>
        <div className="md:col-start-3 md:row-start-2 aspect-square overflow-hidden">
          <LayeredImage layers={slotPreviewLayers(0)} alt={slotSelectedProducts[0]?.slot.name || ''} compact scaleClass={imgScaleClass} />
        </div>
        <div className="md:col-start-3 md:row-start-3 md:mb-0">
          {slotSelectedProducts[0] && (
            <SlotDropdown
              slot={slotSelectedProducts[0].slot}
              selectedProductId={slotSelections[slotSelectedProducts[0].slot.id] || slotSelectedProducts[0].slot.defaultProductId}
              products={slotProductsCache.get(slotSelectedProducts[0].slot.id) || []}
              onChange={(productId) => setSlotSelections(s => ({ ...s, [slotSelectedProducts[0].slot.id]: productId }))}
            />
          )}
        </div>

        {/* + separator (desktop only) */}
        <div className="flex items-center justify-center md:col-start-4 md:row-start-2 md:w-6 lg:w-8 md:self-center my-5 md:my-0">
          <span className="text-4xl md:text-4xl lg:text-5xl text-gray-300 font-light leading-none">+</span>
        </div>

        {/* ───── Col 3: Choix 3ème rabat ───── */}
        <div className="md:col-start-5 md:row-start-1 text-center md:min-h-[4em] md:flex md:flex-col md:items-center md:justify-end">
          <h3 className="uppercase text-xl md:text-[10px] tracking-[0.2em] font-semibold text-gray-700">
            Choix 3ème rabat
          </h3>
          <PriceLine
            original={slotSelectedProducts[1]?.product?.price || 0}
            discountPercent={bundleConfig.discountPercent}
            fmt={fmt}
            className="mt-1"
          />
        </div>
        <div className="md:col-start-5 md:row-start-2 aspect-square overflow-hidden">
          {slotSelectedProducts[1] && (
            <LayeredImage layers={slotPreviewLayers(1)} alt={slotSelectedProducts[1].slot.name} compact scaleClass={imgScaleClass} />
          )}
        </div>
        <div className="md:col-start-5 md:row-start-3 md:mb-0">
          {slotSelectedProducts[1] && (
            <SlotDropdown
              slot={slotSelectedProducts[1].slot}
              selectedProductId={slotSelections[slotSelectedProducts[1].slot.id] || slotSelectedProducts[1].slot.defaultProductId}
              products={slotProductsCache.get(slotSelectedProducts[1].slot.id) || []}
              onChange={(productId) => setSlotSelections(s => ({ ...s, [slotSelectedProducts[1].slot.id]: productId }))}
            />
          )}
        </div>

        {/* = separator (desktop only) */}
        <div className="flex items-center justify-center md:col-start-6 md:row-start-2 md:w-6 lg:w-8 md:self-center my-5 md:my-0">
          <span className="text-4xl md:text-4xl lg:text-5xl text-gray-300 font-light leading-none">=</span>
        </div>

        {/* ───── Col 4: Votre lot ───── */}
        <div className="md:col-start-7 md:row-start-1 text-center md:min-h-[4em] md:flex md:flex-col md:items-center md:justify-end">
          <h3 className="uppercase text-xl md:text-[10px] tracking-[0.2em] font-semibold text-gray-700">
            Votre lot
          </h3>
        </div>
        <div className="md:col-start-7 md:row-start-2 self-stretch bg-gray-50 rounded-[2px] p-4 md:p-6 flex flex-col justify-center text-center text-sm">
          {bundleConfig.discountPercent > 0 && (
            <div className="text-[#9b2b2a] font-semibold text-base">
              −{bundleConfig.discountPercent}%
            </div>
          )}
          <div className="text-gray-700">
            Prix total :{' '}
            <span className="relative inline-block px-0.5 text-gray-500">
              {fmt(subtotal)}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
              >
                <span className="block w-full h-px bg-gray-500 transform -rotate-[8deg]" />
              </span>
            </span>
          </div>
          <div className="mt-3 font-semibold text-gray-900 text-base">
            Prix du lot : {fmt(discounted)}
          </div>
          {savings > 0 && (
            <div className="mt-3 text-[#9b2b2a]">
              <div>Vous économisez :</div>
              <div className="font-semibold">{fmt(savings)}</div>
            </div>
          )}
          {/* Site-wide free relay shipping threshold: 149.99 € */}
          {discounted > 14999 && (
            <div className="mt-3 text-gray-700">
              + Frais de port offerts en point relais
            </div>
          )}
        </div>
        {/* In-grid CTA — desktop only. Mobile gets a sticky bottom CTA below. */}
        <button
          onClick={handleAddToCart}
          disabled={cartLoading}
          className="hidden md:flex md:col-start-7 md:row-start-3 w-full min-h-[60px] items-center justify-center bg-[#171717] text-white px-4 rounded-[2px] font-medium text-sm border-2 border-[#171717] hover:bg-[#2a2a2a] hover:border-[#2a2a2a] disabled:opacity-50 transition-colors"
        >
          {cartLoading ? 'Ajout en cours…' : cartAdded ? '✓ Lot ajouté !' : 'Ajouter le lot au panier'}
        </button>
      </div>

      {/* Extras section — let the customer add more accessories at the same discount */}
      <ExtrasSection
        config={config}
        productMap={productMap}
        extras={extras}
        extrasOpen={extrasOpen}
        discountPercent={bundleConfig.discountPercent}
        usedProductIdsByCategory={usedProductIdsByCategory}
        fmt={fmt}
        onOpenExtras={() => setExtrasOpen(true)}
        onAddExtra={addExtra}
        onUpdateExtra={updateExtra}
        onRemoveExtra={removeExtra}
      />

      {/* Sticky bottom CTA — mobile only, fixed to viewport bottom. */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
      >
        <button
          onClick={handleAddToCart}
          disabled={cartLoading}
          className="w-full min-h-[56px] flex items-center justify-center gap-2 bg-[#171717] text-white px-4 rounded-[2px] font-medium text-sm border-2 border-[#171717] disabled:opacity-50 transition-colors"
        >
          {cartLoading ? (
            'Ajout en cours…'
          ) : cartAdded ? (
            '✓ Lot ajouté !'
          ) : (
            <>
              <span>Ajouter le lot au panier</span>
              <span className="font-semibold">· {fmt(discounted)}</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

function ExtrasSection({
  config,
  productMap,
  extras,
  extrasOpen,
  discountPercent,
  usedProductIdsByCategory,
  fmt,
  onOpenExtras,
  onAddExtra,
  onUpdateExtra,
  onRemoveExtra,
}: {
  config: { categories: Array<{ id: string; name: string; products: ConfigProduct[] }> }
  productMap: Map<string, ConfigProduct>
  extras: ExtraItem[]
  extrasOpen: boolean
  discountPercent: number
  usedProductIdsByCategory: Map<string, Set<string>>
  fmt: (cents: number) => string
  onOpenExtras: () => void
  onAddExtra: (categoryId: string) => void
  onUpdateExtra: (extraId: string, productId: string) => void
  onRemoveExtra: (extraId: string) => void
}) {
  const categoriesWithProducts = config.categories.filter(c => c.products.length > 0)
  if (categoriesWithProducts.length === 0) return null

  // Filter products available for a given extra: exclude IDs used elsewhere
  // (other slots or other extras of the same category), but keep the
  // currently-selected product so the dropdown can still display it.
  const availableForExtra = (extra: ExtraItem): ConfigProduct[] => {
    const cat = config.categories.find(c => c.id === extra.categoryId)
    if (!cat) return []
    const used = usedProductIdsByCategory.get(extra.categoryId) || new Set()
    return cat.products.filter(p => p.id === extra.productId || !used.has(p.id))
  }

  // Number of remaining (not-yet-used) products in a category — for disabling
  // the "+ Ajouter X" button when the category is exhausted.
  const remainingInCategory = (categoryId: string): number => {
    const cat = config.categories.find(c => c.id === categoryId)
    if (!cat) return 0
    const used = usedProductIdsByCategory.get(categoryId) || new Set()
    return cat.products.filter(p => !used.has(p.id)).length
  }

  if (!extrasOpen) {
    return (
      <div className="mt-4 lg:mt-6 border-t border-gray-200 pt-4 lg:pt-5 text-center">
        <p className="text-base lg:text-lg text-gray-900 mb-4">
          Voulez-vous profiter de <span className="text-[#9b2b2a] font-semibold">−{discountPercent}%</span> sur d&apos;autres accessoires&nbsp;?
        </p>
        <button
          type="button"
          onClick={onOpenExtras}
          className="inline-flex items-center justify-center bg-[#171717] text-white px-8 py-3 rounded-[2px] font-medium text-sm border-2 border-[#171717] hover:bg-[#2a2a2a] hover:border-[#2a2a2a] transition-colors"
        >
          Je veux
        </button>
      </div>
    )
  }

  return (
    <div className="mt-4 lg:mt-6 border-t border-gray-200 pt-4 lg:pt-5">
      <p className="uppercase text-[10px] tracking-[0.2em] font-semibold text-gray-700 text-center mb-2">
        Mes accessoires extras
      </p>
      <p className="text-sm text-gray-600 text-center mb-5">
        Tous à <span className="text-[#9b2b2a] font-semibold">−{discountPercent}%</span>. Ajoutez ce que vous voulez.
      </p>

      {/* Category add buttons */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {categoriesWithProducts.map(cat => {
          const remaining = remainingInCategory(cat.id)
          const exhausted = remaining === 0
          return (
            <button
              key={cat.id}
              type="button"
              disabled={exhausted}
              onClick={() => onAddExtra(cat.id)}
              className={`inline-flex items-center gap-1.5 border-2 transition-colors rounded-[2px] px-3 py-2 text-sm font-medium ${
                exhausted
                  ? 'border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-200 hover:border-gray-900 text-gray-900 bg-white'
              }`}
              title={exhausted ? 'Plus de produit disponible dans cette catégorie' : undefined}
            >
              <span className="text-base leading-none">+</span>
              <span>Ajouter {cat.name.toLowerCase()}</span>
            </button>
          )
        })}
      </div>

      {/* List of added extras */}
      {extras.length > 0 && (
        <div className="max-w-2xl mx-auto space-y-3 md:space-y-2">
          {extras.map(extra => {
            const cat = config.categories.find(c => c.id === extra.categoryId)
            if (!cat) return null
            const selectedProduct = productMap.get(extra.productId)
            const price = selectedProduct?.price || 0
            return (
              <div key={extra.id} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2 min-w-0">
                <div className="md:w-32 md:flex-shrink-0">
                  <div className="uppercase text-[10px] tracking-[0.16em] font-semibold text-gray-500">
                    {cat.name}
                  </div>
                  <PriceLine
                    original={price}
                    discountPercent={discountPercent}
                    fmt={fmt}
                    className="mt-0.5"
                  />
                </div>
                <div className="flex items-center gap-2 min-w-0 md:flex-1">
                  <div className="flex-1 min-w-0">
                    <SlotDropdown
                      slot={{ id: extra.id, name: cat.name }}
                      selectedProductId={extra.productId}
                      products={availableForExtra(extra)}
                      onChange={(productId) => onUpdateExtra(extra.id, productId)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveExtra(extra.id)}
                    className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors px-2 py-1 text-xl leading-none"
                    title="Retirer cet accessoire"
                  >
                    ×
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Inline strike-through original + discounted price block, used under
 * each column header.
 */
function PriceLine({
  original,
  discountPercent,
  fmt,
  className = '',
}: {
  original: number
  discountPercent: number
  fmt: (cents: number) => string
  className?: string
}) {
  if (!original || original <= 0) return null
  const discounted = original * (1 - discountPercent / 100)
  if (discountPercent <= 0) {
    return <div className={`text-xs text-gray-700 ${className}`}>{fmt(original)}</div>
  }
  return (
    <div className={`text-xs whitespace-nowrap ${className}`}>
      <span className="text-gray-400 line-through mr-1.5">{fmt(original)}</span>
      <span className="text-[#9b2b2a] font-semibold">{fmt(discounted)}</span>
    </div>
  )
}

function LayeredImage({
  layers,
  alt,
  scaleClass = 'md:scale-[1.2]',
}: {
  layers: Array<{ image: string; layer: number; key: string; opacity?: number }>
  alt: string
  compact?: boolean
  scaleClass?: string
}) {
  return (
    <div className="relative w-full h-full">
      {layers.map(l => (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={l.key}
          src={l.image}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-contain ${scaleClass}`}
          style={{ zIndex: l.layer, opacity: l.opacity ?? 1 }}
          loading="eager"
        />
      ))}
    </div>
  )
}

function SlotDropdown({
  slot,
  selectedProductId,
  products,
  onChange,
}: {
  slot: { id: string; name: string }
  selectedProductId: string
  products: ConfigProduct[]
  onChange: (productId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const selected = products.find(p => p.id === selectedProductId)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 border rounded-[2px] px-2 py-2 bg-white text-left transition-colors ${
          open ? 'border-gray-900' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="w-10 h-10 flex-shrink-0 overflow-hidden bg-gray-100 rounded-[2px]">
          {selected?.thumb && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={selected.thumb} alt="" className="w-full h-full object-cover" />
          )}
        </div>
        <span className="flex-1 text-sm font-medium truncate">{selected?.name || slot.name}</span>
        <span className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-[2px] shadow-lg max-h-80 overflow-y-auto">
          {products.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">Aucune option disponible</div>
          ) : (
            products.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange(p.id)
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-3 px-2 py-2 text-left transition-colors ${
                  p.id === selectedProductId ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <div className="w-10 h-10 flex-shrink-0 overflow-hidden bg-gray-100 rounded-[2px]">
                  {p.thumb && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={p.thumb} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <span className="flex-1 text-sm font-medium">{p.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
